import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { KktFindInterface } from '@zaoblako/shared/modules/finance/dtos/kkt/kkt-find.dto';
import { KktFormDto, KktFormInterface } from '@zaoblako/shared/modules/finance/dtos/kkt/kkt-form.dto';
import type { OperatorFindInterface } from '@zaoblako/shared/modules/finance/dtos/operator/operator-find.dto';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { KktService } from '@zaoblako/web/core/services/ofd/kkt.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { OperatorSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operator-single-select/operator-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';
import { FdoAccessService } from '@zaoblako/web/core/models/enums/access-rules/fdo/fdo-access.service';

@Component({
  selector: 'crm-kkt-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    FormsModule,
    JoinPipe,
    ButtonComponent,
    SpinnerComponent,
    OperatorSingleSelectComponent,
    ContragentAutoCompleteComponent
  ],
  templateUrl: './kkt-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KktFormComponent extends BaseFormComponent<KktFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public kkt: KktFormInterface = {
    serialNumber: null,
    regNumber: null,
    fnNumber: null,
    contragent: null
  };

  public dto = KktFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly contragentService: ContragentService,
    private readonly kktService: KktService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    public readonly fdoAccessService: FdoAccessService
  ) {
    super();
  }

  public ngOnInit(): void {
    const contragentId = this.formStoreService.getState.forms[this.index]?.data?.contragentId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(contragentId)) {
      this.contragentService.findById<ContragentFindInterface>(contragentId).subscribe((contragent) => {
        this.kkt.contragent = contragent;
        this.cdRef.markForCheck();
      });
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.kktService.findById<KktFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (kkt) => {
        this.kkt = kkt;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveKkt() {
    if (this.checkValidate(this.kkt).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.kktService.update<KktFormInterface, KktFindInterface>(this.kkt).subscribe({
          next: (kkt) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`KKT с серийным номером "${this.kkt?.serialNumber}" обновлена!`);
            this.formStoreService.saveNext(kkt, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.kktService.createOne(this.kkt).subscribe({
          next: (kkt) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('ККТ создана!');
            this.formStoreService.saveNext(kkt, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteKkt() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить ККТ с серийным номером "${this.kkt?.serialNumber}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.kktService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`KKT с серийным номером "${this.kkt?.serialNumber}" удалена!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public get operator(): OperatorFindInterface {
    return this.kkt?.operator as OperatorFindInterface;
  }

  public set operator(value: OperatorFindInterface) {
    if (this.kkt) {
      this.kkt.operator = value;
    }
  }
}
